import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "codex_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

// NOTE: The app is served same-origin with the backend (/api on the same domain),
// so the Google httpOnly session cookie is sent automatically. We intentionally do
// NOT set withCredentials globally, to avoid the ingress `ACAO: *` + credentials conflict.
const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---- Content ----
export const getHome = () => client.get("/home").then((r) => r.data);
export const getMeta = () => client.get("/meta").then((r) => r.data);
export const getCategories = () => client.get("/categories").then((r) => r.data);
export const listTopics = (params) => client.get("/topics", { params }).then((r) => r.data);
export const getTopic = (slug) => client.get(`/topics/${slug}`).then((r) => r.data);
export const getTopicCore = (slug) => client.get(`/topics/${slug}/core`).then((r) => r.data);
export const getTopicSection = (slug, section) => client.get(`/topics/${slug}/section/${section}`).then((r) => r.data);
export const quickSearch = (q) => client.get("/search", { params: { q } }).then((r) => r.data);
export const getRandom = () => client.get("/random").then((r) => r.data);
export const getFeaturedCollections = () => client.get("/collections/featured").then((r) => r.data);

// ---- Graph ----
export const getConstellation = (slug, depth = 1) =>
  client.get(`/graph/topic/${slug}`, { params: { depth } }).then((r) => r.data);
export const getUniverse = (params) => client.get("/graph/universe", { params }).then((r) => r.data);

// ---- AI ----
export const aiSearch = (query) => client.post("/ai/search", { query }, { timeout: 90000 }).then((r) => r.data);
export const goDeeper = (payload) => client.post("/ai/go-deeper", payload, { timeout: 60000 }).then((r) => r.data);

// ---- Auth ----
export const authRegister = (body) => client.post("/auth/register", body).then((r) => r.data);
export const authLogin = (body) => client.post("/auth/login", body).then((r) => r.data);
export const authMe = () => client.get("/auth/me").then((r) => r.data);
export const authGoogleSession = (session_id) => client.post("/auth/session", { session_id }).then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);

// ---- User data ----
export const listBookmarks = () => client.get("/bookmarks").then((r) => r.data);
export const checkBookmark = (slug) => client.get(`/bookmarks/check/${slug}`).then((r) => r.data);
export const addBookmark = (slug) => client.post("/bookmarks", { topic_slug: slug }).then((r) => r.data);
export const removeBookmark = (slug) => client.delete(`/bookmarks/${slug}`).then((r) => r.data);

export const listNotes = () => client.get("/notes").then((r) => r.data);
export const getNote = (slug) => client.get(`/notes/${slug}`).then((r) => r.data);
export const saveNote = (slug, content) => client.put("/notes", { topic_slug: slug, content }).then((r) => r.data);
export const deleteNote = (slug) => client.delete(`/notes/${slug}`).then((r) => r.data);

export const listCollections = () => client.get("/collections").then((r) => r.data);
export const createCollection = (body) => client.post("/collections", body).then((r) => r.data);
export const updateCollection = (id, body) => client.put(`/collections/${id}`, body).then((r) => r.data);
export const deleteCollection = (id) => client.delete(`/collections/${id}`).then((r) => r.data);

export const getReadingHistory = () => client.get("/reading-history").then((r) => r.data);
export const addReadingHistory = (slug) => client.post("/reading-history", { topic_slug: slug }).then((r) => r.data);

export const addTopicToCollection = (cid, slug) => client.post(`/collections/${cid}/topics`, { topic_slug: slug }).then((r) => r.data);
export const removeTopicFromCollection = (cid, slug) => client.delete(`/collections/${cid}/topics/${slug}`).then((r) => r.data);

// ---- Phase 4: timelines / maps / contributions ----
export const getTimelines = (params) => client.get("/timelines", { params }).then((r) => r.data);
export const getMapSites = (kind = "") => client.get("/maps/sites", { params: { kind } }).then((r) => r.data);
export const graphExpand = (slug, exclude = "") => client.get(`/graph/expand/${slug}`, { params: { exclude } }).then((r) => r.data);

export const createContribution = (body) => client.post("/contributions", body).then((r) => r.data);
export const myContributions = () => client.get("/contributions/mine").then((r) => r.data);
export const moderationQueue = (status = "pending") => client.get("/contributions/moderation", { params: { status } }).then((r) => r.data);
export const checkModerator = () => client.get("/contributions/is-moderator").then((r) => r.data);
export const reviewContribution = (id, body) => client.post(`/contributions/${id}/review`, body).then((r) => r.data);
export const approveAndGenerate = (id) => client.post(`/contributions/${id}/approve-generate`).then((r) => r.data);

// ---- The Archivist: premium narration (ElevenLabs proxy) ----
export const getArchivistVoices = () => client.get("/archivist/voices").then((r) => r.data);
export const narrateArchivist = (body) => client.post("/archivist/narrate", body, { responseType: "blob" }).then((r) => r.data);
export const narrateTopic = (slug, voiceId) => client.post(`/archivist/narrate/topic/${slug}`, null, voiceId ? { params: { voice_id: voiceId } } : {}).then((r) => r.data);
export const archivistAudioUrl = (key) => `${API}/archivist/audio/${key}`;
export const tourAudio = (slug) => client.get("/archivist/tour-audio", { params: { slug } }).then((r) => r.data);
export const atlasStopAudio = (slug, name = "") => client.get("/archivist/atlas-audio", { params: { slug, name } }).then((r) => r.data);
export const audioAllowance = () => client.get("/archivist/audio/allowance").then((r) => r.data);
export const audioProgress = (granted_seconds, played_seconds) =>
  client.post("/archivist/audio/progress", { granted_seconds, played_seconds }).then((r) => r.data).catch(() => null);
export const archivistInvitation = () => client.get("/archivist/invitation", { timeout: 90000 }).then((r) => r.data);
export const listDraftTopics = (status = "draft") => client.get("/moderation/drafts", { params: { status } }).then((r) => r.data);

// ---- Discussion / Comments ----
export const listComments = (slug) => client.get(`/topics/${slug}/comments`).then((r) => r.data);
export const createComment = (slug, body) => client.post(`/topics/${slug}/comments`, body).then((r) => r.data);
export const deleteComment = (id) => client.delete(`/comments/${id}`).then((r) => r.data);
export const uploadCommentImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client
    .post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 })
    .then((r) => r.data);
};
// Uploaded file paths come back as "/api/files/..."; resolve to an absolute URL.
export const fileUrl = (path) => (!path ? path : path.startsWith("http") ? path : `${BACKEND_URL}${path}`);

// Legacy hero images were saved to local disk at "/images/topics/<slug>.png",
// which does NOT survive container redeploys. Rewrite those to the durable
// object-storage route so hero images load correctly everywhere — even before a
// database migration reaches a given environment (e.g. production).
export const heroUrl = (path) => {
  if (!path || typeof path !== "string") return path;
  const m = path.match(/^\/images\/topics\/(.+)$/);
  return m ? `/api/files/codex/hero/${m[1]}` : path;
};

// ---- Notifications (staff only) ----
export const getNotifications = () => client.get("/notifications").then((r) => r.data);
export const markAllNotificationsRead = () => client.post("/notifications/read-all").then((r) => r.data);
export const markNotificationRead = (id) => client.post(`/notifications/${id}/read`).then((r) => r.data);

// ---- Direct Messages (1:1 DMs) ----
export const dmProfile = () => client.get("/dm/profile").then((r) => r.data);
export const dmSetUsername = (username) => client.post("/dm/username", { username }).then((r) => r.data);
export const dmCheckUsername = (u) => client.get("/dm/username/check", { params: { u } }).then((r) => r.data);
export const dmUpdateSettings = (allow_dms) => client.put("/dm/settings", { allow_dms }).then((r) => r.data);
export const dmSearchUsers = (q) => client.get("/dm/users/search", { params: { q } }).then((r) => r.data);
export const dmListConversations = () => client.get("/dm/conversations").then((r) => r.data);
export const dmStartConversation = (payload) => client.post("/dm/conversations", payload).then((r) => r.data);
export const dmGetConversation = (cid) => client.get(`/dm/conversations/${cid}`).then((r) => r.data);
export const dmListMessages = (cid, params = {}) => client.get(`/dm/conversations/${cid}/messages`, { params }).then((r) => r.data);
export const dmSendMessage = (cid, body) => client.post(`/dm/conversations/${cid}/messages`, body).then((r) => r.data);
export const dmMarkRead = (cid) => client.post(`/dm/conversations/${cid}/read`).then((r) => r.data);
export const dmUnreadCount = () => client.get("/dm/unread-count").then((r) => r.data);
export const dmBlock = (user_id) => client.post("/dm/block", { user_id }).then((r) => r.data);
export const dmUnblock = (user_id) => client.delete(`/dm/block/${user_id}`).then((r) => r.data);
export const dmListBlocks = () => client.get("/dm/blocks").then((r) => r.data);
export const dmReport = (body) => client.post("/dm/report", body).then((r) => r.data);

// ---- Analytics (best-effort; never throws) ----
export const trackEvent = (body) => client.post("/analytics/track", body).then((r) => r.data).catch(() => null);

// ---- Store (private merch — allowlisted) ----
export const storeAccess = () => client.get("/store/access").then((r) => r.data).catch(() => ({ access: false }));
export const storeConfig = () => client.get("/store/config").then((r) => r.data);
export const storeProducts = () => client.get("/store/products").then((r) => r.data);
export const storeProduct = (id) => client.get(`/store/products/${id}`).then((r) => r.data);
export const storeNotify = (body) => client.post("/store/notify", body).then((r) => r.data);
export const getFieldGear = (slug = "codex-field-gear") => client.get(`/store/collection/${slug}`).then((r) => r.data);
export const getCaseArtifact = (topicSlug) => client.get(`/store/case-artifact/${topicSlug}`).then((r) => r.data).catch(() => ({ product: null }));

// ---- Narration A/B pilot (admin) ----
export const getNarrationPilot = () => client.get("/admin/narration-pilot").then((r) => r.data);
export const fetchPilotAudioBlob = (id) =>
  client.get(`/admin/narration-pilot/audio/${id}`, { responseType: "blob" })
    .then((r) => URL.createObjectURL(r.data));

// ---- Subscriptions (Full Archive Access) ----
export const getSubscription = () => client.get("/subscription").then((r) => r.data);
export const createCheckout = (lookup_key) =>
  client.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }, { timeout: 30000 }).then((r) => r.data);
export const getPaymentStatus = (sid) => client.get(`/payments/status/${sid}`).then((r) => r.data);
export const openBillingPortal = () =>
  client.post("/payments/portal", { origin_url: window.location.origin }).then((r) => r.data);
export const restorePurchases = () => client.post("/payments/restore").then((r) => r.data);

// ---- Admin / Staff ----
export const getMetrics = () => client.get("/admin/metrics").then((r) => r.data);
export const getAudioPlays = (limit = 50) => client.get("/admin/audio-plays", { params: { limit } }).then((r) => r.data);
export const listUsers = () => client.get("/admin/users").then((r) => r.data);
export const setUserRole = (id, role) => client.post(`/admin/users/${id}/role`, { role }).then((r) => r.data);
export const banUser = (id, banned, reason = "") => client.post(`/admin/users/${id}/ban`, { banned, reason }).then((r) => r.data);
export const setTopicLocation = (slug, body) => client.put(`/admin/topics/${slug}/location`, body).then((r) => r.data);
export const removeTopicLocation = (slug) => client.delete(`/admin/topics/${slug}/location`).then((r) => r.data);
export const suggestTopicLocation = (slug, body) =>
  client.post(`/contributions`, { type: "suggest_location", topic_slug: slug, ...body }).then((r) => r.data);

// ---- Admin: freemium access control ----
export const getAccessConfig = () => client.get("/admin/access/config").then((r) => r.data);
export const listAccessFiles = (params) => client.get("/admin/access/files", { params }).then((r) => r.data);
export const setTopicAccess = (slug, body) => client.put(`/admin/topics/${slug}/access`, body).then((r) => r.data);
export const getUnsealed = () => client.get("/admin/unsealed").then((r) => r.data);
export const setUnsealed = (body) => client.put("/admin/unsealed", body).then((r) => r.data);
export const clearUnsealed = () => client.delete("/admin/unsealed").then((r) => r.data);

// ---- The Archivist AI: grounded, cited, quota-capped Q&A ----
export const archivistQuota = () => client.get("/ai/archivist/quota").then((r) => r.data);
export const askArchivist = (body) => client.post("/ai/archivist", body, { timeout: 90000 }).then((r) => r.data);

// ---- Admin: membership (complimentary access + AI allowance + limits + usage) ----
export const adminSearchMembers = (q = "") => client.get("/admin/users/search", { params: { q } }).then((r) => r.data);
export const adminSetComplimentary = (id, body) => client.post(`/admin/users/${id}/complimentary`, body).then((r) => r.data);
export const adminSetAiAllowance = (id, ai_allowance) => client.put(`/admin/users/${id}/ai-allowance`, { ai_allowance }).then((r) => r.data);
export const adminGetAiLimits = () => client.get("/admin/ai-limits").then((r) => r.data);
export const adminSetAiLimits = (body) => client.put("/admin/ai-limits", body).then((r) => r.data);
export const adminGetAiUsage = () => client.get("/admin/ai-usage").then((r) => r.data);

// ---- Admin: Explorer limits + Today's Mystery (daily free file) ----
export const adminGetExplorerLimits = () => client.get("/admin/explorer-limits").then((r) => r.data);
export const adminSetExplorerLimits = (body) => client.put("/admin/explorer-limits", body).then((r) => r.data);
export const adminGetTodaysMystery = () => client.get("/admin/todays-mystery").then((r) => r.data);
export const adminSetTodaysMystery = (body) => client.put("/admin/todays-mystery", body).then((r) => r.data);
export const adminScheduleMystery = (date, slug) => client.post("/admin/todays-mystery/schedule", { date, slug }).then((r) => r.data);
export const adminUnscheduleMystery = (date) => client.delete(`/admin/todays-mystery/schedule/${date}`).then((r) => r.data);

export default client;
